/**
 * waap-bot-management-tab.tsx — Bot Management tab (B4 batch).
 *
 * Account-level bot strategies: list, create, edit, delete.
 * Each strategy has browser interrogation + request whitelist fields.
 * Service-scoped endpoints. All mutations via ActAsMutationConfirm.
 *
 * Endpoints:
 *   GET    /services/:serviceId/waap/bot-strategies
 *   POST   /services/:serviceId/waap/bot-strategies
 *   PUT    /services/:serviceId/waap/bot-strategies/:sid
 *   DELETE /services/:serviceId/waap/bot-strategies/:sid
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus, Pencil, Trash2, Bot } from 'lucide-react';
import {
  Card, CardHeader, CardTitle, CardBody,
  Button, IconButton, Input, Textarea, Badge, Skeleton,
} from '@vnetwork/ui/primitives';
import { EmptyState } from '@vnetwork/ui/data';
import { Field } from '@vnetwork/ui/forms';
import { TabError } from '@/pages/customers/detail/tab-states';
import { ActAsMutationConfirm } from '@/pages/operations/detail/cdn/act-as-mutation-confirm';
import { actAsClient } from '@/api/act-as-client';
import { actAsWAAPKeys } from '@/api/act-as-waap';
import {
  useActAsWAAPBotStrategies,
  type BotStrategy,
} from '@/api/act-as-waap-rules';

/* ── Helpers ─────────────────────────────────────────────────────────────── */

function parseIdList(text: string): number[] {
  return text
    .split(/[\s,]+/)
    .map((s) => parseInt(s.trim(), 10))
    .filter((n) => !isNaN(n));
}

const EMPTY_STRATEGY: BotStrategy = {
  name: '',
  description: '',
  sites: [],
  browser_interrogation: [],
  request_whitelist: [],
};

interface Pending {
  kind: 'create' | 'update' | 'delete';
  strategy: BotStrategy;
}

/* ── Strategy form — extracted to keep line count manageable ─────────────── */

interface StrategyFormProps {
  strategy: BotStrategy;
  sitesDraft: string;
  biDraft: string;       // browser_interrogation as JSON
  wlDraft: string;       // request_whitelist as JSON
  onPatch: (key: keyof BotStrategy, value: unknown) => void;
  onSitesDraft: (v: string) => void;
  onBiDraft: (v: string) => void;
  onWlDraft: (v: string) => void;
  onCancel: () => void;
  onSubmit: () => void;
  isEditing: boolean;
}

function StrategyForm({
  strategy,
  sitesDraft,
  biDraft,
  wlDraft,
  onPatch,
  onSitesDraft,
  onBiDraft,
  onWlDraft,
  onCancel,
  onSubmit,
  isEditing,
}: StrategyFormProps) {
  const { t } = useTranslation();
  const isValid = strategy.name.trim() !== '';

  return (
    <Card>
      <CardHeader>
        <CardTitle style={{ fontSize: 'var(--vn-font-size-sm)' }}>
          {isEditing
            ? t('waap_domain.bot_edit', 'Edit bot strategy')
            : t('waap_domain.bot_new', 'New bot strategy')}
        </CardTitle>
      </CardHeader>
      <CardBody style={{ display: 'grid', gap: 'var(--vn-space-3)' }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 'var(--vn-space-3)' }}>
          <Field label={t('waap_domain.bot_field_name', 'Name')}>
            {(p) => (
              <Input
                {...p}
                value={strategy.name}
                onChange={(e) => onPatch('name', e.target.value)}
                placeholder="Strategy name"
              />
            )}
          </Field>
          <Field label={t('waap_domain.bot_field_desc', 'Description')}>
            {(p) => (
              <Input
                {...p}
                value={strategy.description ?? ''}
                onChange={(e) => onPatch('description', e.target.value)}
                placeholder="Optional"
              />
            )}
          </Field>
          <Field label={t('waap_domain.bot_field_sites', 'Site IDs (comma-sep)')}>
            {(p) => (
              <Input
                {...p}
                value={sitesDraft}
                onChange={(e) => onSitesDraft(e.target.value)}
                placeholder="1, 2, 3"
              />
            )}
          </Field>
        </div>

        <Field
          label={t('waap_domain.bot_field_bi', 'Browser Interrogation (JSON array)')}
          description={t('waap_domain.bot_field_bi_desc', 'Array of browser interrogation config objects. See vendor bot docs for the per-entry shape.')}
        >
          {(p) => (
            <Textarea
              {...p}
              value={biDraft}
              onChange={(e) => onBiDraft(e.target.value)}
              rows={4}
              style={{ fontFamily: 'monospace', fontSize: 'var(--vn-font-size-xs)' }}
            />
          )}
        </Field>

        <Field
          label={t('waap_domain.bot_field_wl', 'Request Whitelist (JSON array)')}
          description={t('waap_domain.bot_field_wl_desc', 'Array of request whitelist entries. Whitelisted requests bypass bot detection.')}
        >
          {(p) => (
            <Textarea
              {...p}
              value={wlDraft}
              onChange={(e) => onWlDraft(e.target.value)}
              rows={4}
              style={{ fontFamily: 'monospace', fontSize: 'var(--vn-font-size-xs)' }}
            />
          )}
        </Field>

        <div style={{ display: 'flex', gap: 'var(--vn-space-2)', justifyContent: 'flex-end' }}>
          <Button size="sm" variant="secondary" onClick={onCancel}>
            {t('common.cancel', 'Cancel')}
          </Button>
          <Button size="sm" variant="primary" disabled={!isValid} onClick={onSubmit}>
            {isEditing ? t('common.save', 'Save') : t('common.create', 'Create')}
          </Button>
        </div>
      </CardBody>
    </Card>
  );
}

/* ── Root component ──────────────────────────────────────────────────────── */

interface WaapBotManagementTabProps {
  serviceId: string;
}

/**
 * Bot Management tab — list + CRUD for service-scoped bot strategies.
 * Browser interrogation and request whitelist as editable JSON arrays.
 */
export function WaapBotManagementTab({ serviceId }: WaapBotManagementTabProps) {
  const { t } = useTranslation();
  const strategiesQ = useActAsWAAPBotStrategies(serviceId);

  const [formOpen, setFormOpen]     = useState(false);
  const [editing, setEditing]       = useState<BotStrategy | null>(null);
  const [draft, setDraft]           = useState<BotStrategy>(EMPTY_STRATEGY);
  const [sitesDraft, setSitesDraft] = useState('');
  const [biDraft, setBiDraft]       = useState('[]');
  const [wlDraft, setWlDraft]       = useState('[]');
  const [pending, setPending]       = useState<Pending | null>(null);

  function openNew() {
    setEditing(null);
    setDraft({ ...EMPTY_STRATEGY });
    setSitesDraft('');
    setBiDraft('[]');
    setWlDraft('[]');
    setFormOpen(true);
  }

  function openEdit(s: BotStrategy) {
    setEditing(s);
    setDraft({ ...s });
    setSitesDraft((s.sites ?? []).join(', '));
    setBiDraft(JSON.stringify(s.browser_interrogation ?? [], null, 2));
    setWlDraft(JSON.stringify(s.request_whitelist ?? [], null, 2));
    setFormOpen(true);
  }

  function closeForm() {
    setFormOpen(false);
    setEditing(null);
  }

  function safeParseArray(text: string): unknown[] {
    try {
      const v = JSON.parse(text);
      return Array.isArray(v) ? v : [];
    } catch { return []; }
  }

  function handleSubmit() {
    const payload: BotStrategy = {
      ...draft,
      sites: parseIdList(sitesDraft),
      browser_interrogation: safeParseArray(biDraft) as BotStrategy['browser_interrogation'],
      request_whitelist: safeParseArray(wlDraft) as BotStrategy['request_whitelist'],
    };
    setPending({ kind: editing ? 'update' : 'create', strategy: payload });
  }

  function buildMutationFn(p: Pending) {
    const base = `/services/${serviceId}/waap/bot-strategies`;
    if (p.kind === 'create') return () => actAsClient.post(base, p.strategy);
    if (p.kind === 'update') return () => actAsClient.put(`${base}/${p.strategy.id}`, p.strategy);
    return () => actAsClient.delete(`${base}/${p.strategy.id}`);
  }

  function patch(key: keyof BotStrategy, value: unknown) {
    setDraft((prev) => ({ ...prev, [key]: value }));
  }

  if (strategiesQ.isLoading) return <Skeleton style={{ width: '100%', height: 120 }} />;
  if (strategiesQ.isError) {
    return (
      <TabError
        title={t('waap_domain.bot_load_error', 'Failed to load bot strategies')}
        onRetry={() => strategiesQ.refetch()}
      />
    );
  }

  const strategies = strategiesQ.data ?? [];

  return (
    <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <CardTitle style={{ display: 'flex', alignItems: 'center', gap: 'var(--vn-space-2)', margin: 0 }}>
          <Bot size={15} />
          {t('waap_domain.bot_title', 'Bot Strategies')}
          <Badge style={{ marginLeft: 'var(--vn-space-1)' }}>{strategies.length}</Badge>
        </CardTitle>
        <Button size="sm" variant="secondary" onClick={openNew}>
          <Plus size={13} />
          {t('waap_domain.bot_add', 'Add strategy')}
        </Button>
      </div>

      {strategies.length === 0 && !formOpen ? (
        <EmptyState
          title={t('waap_domain.bot_empty', 'No bot strategies')}
          description={t('waap_domain.bot_empty_desc', 'Create a bot strategy to manage automated traffic with browser interrogation and whitelisting.')}
        />
      ) : (
        <div style={{ display: 'grid', gap: 'var(--vn-space-2)' }}>
          {strategies.map((s) => (
            <Card key={s.id ?? s.name}>
              <CardBody style={{ display: 'flex', alignItems: 'center', gap: 'var(--vn-space-3)', flexWrap: 'wrap' }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 600, fontSize: 'var(--vn-font-size-sm)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {s.name}
                  </div>
                  <div style={{ fontSize: 'var(--vn-font-size-xs)', color: 'var(--vn-fg-muted)', marginTop: 2 }}>
                    {t('waap_domain.bot_bi_count', '{{n}} browser interrogation rule(s)', {
                      n: s.browser_interrogation?.length ?? 0,
                    })}
                    {' · '}
                    {t('waap_domain.bot_wl_count', '{{n}} whitelist rule(s)', {
                      n: s.request_whitelist?.length ?? 0,
                    })}
                    {(s.sites?.length ?? 0) > 0 && ` · sites: ${s.sites!.join(', ')}`}
                  </div>
                  {s.description && (
                    <div style={{ fontSize: 'var(--vn-font-size-xs)', color: 'var(--vn-fg-subtle)' }}>
                      {s.description}
                    </div>
                  )}
                </div>
                <div style={{ display: 'flex', gap: 'var(--vn-space-1)' }}>
                  <IconButton size="sm" variant="ghost" aria-label={t('common.edit', 'Edit')} onClick={() => openEdit(s)}>
                    <Pencil size={13} />
                  </IconButton>
                  <IconButton
                    size="sm"
                    variant="ghost"
                    aria-label={t('common.delete', 'Delete')}
                    onClick={() => setPending({ kind: 'delete', strategy: s })}
                  >
                    <Trash2 size={13} />
                  </IconButton>
                </div>
              </CardBody>
            </Card>
          ))}
        </div>
      )}

      {formOpen && (
        <StrategyForm
          strategy={draft}
          sitesDraft={sitesDraft}
          biDraft={biDraft}
          wlDraft={wlDraft}
          onPatch={patch}
          onSitesDraft={setSitesDraft}
          onBiDraft={setBiDraft}
          onWlDraft={setWlDraft}
          onCancel={closeForm}
          onSubmit={handleSubmit}
          isEditing={!!editing}
        />
      )}

      {pending && (
        <ActAsMutationConfirm
          open
          onOpenChange={(open) => { if (!open) setPending(null); }}
          title={
            pending.kind === 'delete'
              ? t('waap_domain.bot_delete_title', 'Delete bot strategy')
              : pending.kind === 'create'
              ? t('waap_domain.bot_create_title', 'Create bot strategy')
              : t('waap_domain.bot_update_title', 'Update bot strategy')
          }
          summary={
            pending.kind === 'delete'
              ? t('waap_domain.bot_delete_summary', 'Permanently delete bot strategy "{{name}}".', { name: pending.strategy.name })
              : t('waap_domain.bot_save_summary', 'Save bot strategy "{{name}}".', { name: pending.strategy.name })
          }
          destructive={pending.kind === 'delete'}
          queryKeysToInvalidate={[actAsWAAPKeys.botStrategies(serviceId)]}
          mutationFn={buildMutationFn(pending)}
          onSuccess={() => { setPending(null); closeForm(); strategiesQ.refetch(); }}
        />
      )}
    </div>
  );
}
